import { useCallback } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ExportDTO, GraphicTemplateDTO, RightsSummary, SequenceDocument, SequenceDuplicateResponse } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { usePaged } from './paged';
import { sourceStillChanging } from '../../lib/sources';

export interface SequenceSummary {
  id: string;
  name: string;
  revision: number;
  durationTicks: number;
  clips: number;
  frameRate: { num: number; den: number };
  width: number;
  height: number;
  updatedAt: string;
  updatedBy: string | null;
  description: string;
  /** Who created the sequence (the owner column), and their user id for "My sequences". */
  createdBy: string | null;
  createdById: string | null;
  /** When the sequence was created (0.91.0). */
  createdAt?: string | null;
  /** Who is editing it now (0.97.0; in the sequence list). */
  lockedBy?: { id: string; displayName: string; since: string } | null;
}

export interface SequenceDetail extends SequenceSummary {
  document: SequenceDocument;
  versions: Array<{ id: string; revision: number; label: string; kind: 'named' | 'export'; createdAt: string; createdBy: string | null }>;
}

export interface SequenceSource {
  versionId: string;
  assetId: string;
  title: string;
  status: string;
  durationTicks: number | null;
  mediaKind: 'video' | 'audio' | 'image' | 'graphic' | null;
  proxyUrl: string | null;
  originalAvailable: boolean;
  originalProblem: string | null;
  /** The original is on a storage agent at this site; exports fetch it for the render. */
  originalOnSite?: string | null;
  /** The picture has an alpha channel (and a transparent proxy). */
  hasAlpha?: boolean;
  /** 1.8.0: the source has sound (an audio stream); a silent one gets no level line, volume keys or crossfade. */
  hasAudio?: boolean;
  startTimecode?: string | null;
  /** The waveform (0.96.0), drawn on the timeline's sound clips. */
  waveformUrl?: string | null;
  /**
   * A version still recording (1.0.0-rc.1): the snapshot playlist of what is recorded so far (signed, closed with
   * #EXT-X-ENDLIST), its length, and whether it still records or is being ingested. Null once it has its proxy.
   */
  live?: { playlistUrl: string; recordedTicks: number; state: 'recording' | 'finishing' } | null;
  /** The displayed picture size (1.7.0): after rotation, in square pixels; null when not known (audio, not probed). */
  width?: number | null;
  height?: number | null;
  /** 1.8.0: the source's effective rights; absent while rights management is off (or for sources the caller can't see). */
  rights?: RightsSummary | null;
}

/**
 * Duplicate a sequence (1.7.4): POST /api/v1/sequences/:id/duplicate makes a copy of its saved document, description
 * and formats ("<name> copy" unless a name is given), without versions, exports or hand-offs. The Create list is
 * refreshed; the caller opens the copy.
 */
export function useDuplicateSequence() {
  const qc = useQueryClient();
  return useCallback(async (id: string, name?: string): Promise<SequenceDuplicateResponse> => {
    const copy = await api<SequenceDuplicateResponse>(`/api/v1/sequences/${id}/duplicate`, { method: 'POST', json: name?.trim() ? { name: name.trim() } : {} });
    await qc.invalidateQueries({ queryKey: queryKeys.sequences() });
    return copy;
  }, [qc]);
}

export function useSequences() {
  return usePaged<SequenceSummary>({ queryKey: queryKeys.sequences(), url: '/api/v1/sequences' });
}

/**
 * A sequence with its saved versions. Never refetched in the background (the editor owns the document while it is
 * open), but always fetched again when the editor opens it (0.97.0): the cached copy may be revisions behind.
 */
export function useSequence(id: string | undefined) {
  return useQuery({ queryKey: queryKeys.sequence(id), queryFn: () => api<SequenceDetail>(`/api/v1/sequences/${id}?versions=named`), enabled: Boolean(id), staleTime: Infinity, refetchOnMount: 'always' });
}

export function useSequenceSources(id: string | undefined, revision: number | undefined) {
  return useQuery({
    queryKey: queryKeys.sequenceSources(id, revision),
    queryFn: () => api<{ items: SequenceSource[]; expiresAt: string }>(`/api/v1/sequences/${id}/sources`),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    // While a source records (1.0.0-rc.1): its length grows, and its clips switch to the proxy once it is made. And
    // until it has finished processing (1.0.0-rc.4): the waveform is made after the proxy, so polling that stopped
    // with the proxy never saw it (on Compose, where the workers are busier, it always came later).
    refetchInterval: (q) => (q.state.data?.items.some(sourceStillChanging) ? 5000 : false),
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: queryKeys.templates(),
    queryFn: () => api<{ items: GraphicTemplateDTO[] }>('/api/v1/graphics/templates'),
    staleTime: 5 * 60_000,
  });
}

export function useTemplateBundle(id: string | undefined, version: number | undefined) {
  return useQuery({
    queryKey: queryKeys.templateBundle(id, version),
    queryFn: async () => {
      const res = await fetch(`/api/v1/graphics/templates/${id}/${version}/bundle`, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('Template could not be loaded');
      return res.text();
    },
    enabled: Boolean(id && version),
    staleTime: Infinity,
  });
}

export function useExports(sequenceId: string | undefined) {
  return usePaged<ExportDTO>({
    queryKey: queryKeys.exports(sequenceId),
    url: `/api/v1/exports?sequenceId=${sequenceId}`,
    enabled: Boolean(sequenceId),
    // While an export renders, and while a finished one is still being saved as an asset (or into a placeholder):
    // that step sends no event of its own. A save stuck for ten minutes is left to a manual refresh.
    refetchInterval: (items) => (items.some((e) => e.status === 'queued' || e.status === 'running'
      || (e.status === 'succeeded' && e.asAsset && !e.asAsset.assetId && !e.asAsset.failed && Date.now() - Date.parse(e.finishedAt ?? e.createdAt) < 600_000)) ? 3000 : false),
  });
}
